import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { MessageCircle, Mail, Phone, HelpCircle, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const faqs = [
  {
    category: 'Getting Started',
    questions: [
      {
        q: 'How do I create my first QR code?',
        a: 'Simply sign up for a free account, click "Create QR Code", choose your content type (URL, vCard, WiFi, etc.), customize the design, and download. It takes less than 2 minutes!',
      },
      {
        q: 'What\'s the difference between static and dynamic QR codes?',
        a: 'Static QR codes have the data embedded directly and cannot be changed after creation. Dynamic QR codes use a short redirect URL, allowing you to edit the destination, track scans, and add features like password protection even after printing.',
      },
      {
        q: 'Do I need a paid plan to create QR codes?',
        a: 'No! Our free plan includes 5 static QR codes, 3 dynamic QR codes, and 50 scans per month with basic analytics. Paid plans offer unlimited codes, advanced features, and higher scan limits.',
      },
    ],
  },
  {
    category: 'Features & Functionality',
    questions: [
      {
        q: 'Can I edit a QR code after printing it?',
        a: 'Yes, with dynamic QR codes! You can change the destination URL, update content, enable/disable the code, add password protection, and modify expiration dates—all without reprinting.',
      },
      {
        q: 'How do I track QR code scans?',
        a: 'Every dynamic QR code includes real-time analytics showing total scans, unique visitors, geographic locations, device types, time patterns, and referrer sources. Access analytics from your dashboard.',
      },
      {
        q: 'Can I customize the design of my QR codes?',
        a: 'Absolutely! Choose from 20+ templates, customize colors, add your logo, select patterns and corner styles, and add frames with call-to-action text. Our AI can even suggest designs based on your brand.',
      },
      {
        q: 'What content types can I encode in QR codes?',
        a: 'We support 25+ content types including URLs, vCards, WiFi credentials, emails, SMS, phone numbers, locations, events, PDFs, videos, social media, app downloads, and more.',
      },
    ],
  },
  {
    category: 'Plans & Pricing',
    questions: [
      {
        q: 'How much does NexQR cost?',
        a: 'We offer 4 plans: Free (5 static + 3 dynamic codes, 50 scans/month), Starter ($9/mo - unlimited codes, 100K scans), Professional ($29/mo - advanced features, unlimited scans), and Business ($79/mo - team features, API access).',
      },
      {
        q: 'Can I upgrade or downgrade my plan?',
        a: 'Yes! You can change plans anytime from your account settings. Upgrades take effect immediately, and downgrades apply at the next billing cycle.',
      },
      {
        q: 'Do you offer refunds?',
        a: 'Please refer to our refund policy page for detailed information about refunds and cancellations.',
      },
      {
        q: 'Is there a discount for annual billing?',
        a: 'Yes! Annual plans get 20% off compared to monthly billing. Plus, you get 2 months free when you pay yearly.',
      },
    ],
  },
  {
    category: 'Technical Support',
    questions: [
      {
        q: 'Why isn\'t my QR code scanning?',
        a: 'Common issues: 1) Code too small (minimum 2x2 cm), 2) Low contrast colors, 3) Damaged or distorted print, 4) Poor lighting. Test with multiple devices and ensure high print quality.',
      },
      {
        q: 'Can I use QR codes for commercial purposes?',
        a: 'Yes! All QR codes you create are yours to use commercially without attribution. We provide full commercial usage rights on all plans.',
      },
      {
        q: 'How secure are dynamic QR codes?',
        a: 'All redirects use SSL encryption (HTTPS), data is GDPR compliant, and we offer password protection, IP whitelisting, and fraud detection on higher plans.',
      },
      {
        q: 'Do QR codes expire?',
        a: 'Free plan codes remain active as long as your account exists. Paid plan codes never expire. You can also set custom expiration dates for specific campaigns.',
      },
    ],
  },
  {
    category: 'Integration & API',
    questions: [
      {
        q: 'Does NexQR offer API access?',
        a: 'Yes! Business and Enterprise plans include full REST API access for QR code generation, analytics retrieval, and batch operations. Documentation is available in your dashboard.',
      },
      {
        q: 'Can I integrate with my CRM?',
        a: 'Yes! We offer native integrations with HubSpot, Salesforce, Zapier, and webhooks for custom integrations. Set up from Settings > Integrations.',
      },
      {
        q: 'Do you support bulk QR code generation?',
        a: 'Yes! Upload a CSV file with your data and generate hundreds of unique QR codes at once. Available on Pro plans and above.',
      },
    ],
  },
];

export default function Support() {
  const [openIndex, setOpenIndex] = useState<string | null>(null);

  const toggleFAQ = (index: string) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20">
        {/* Hero Section */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative py-20 px-4 overflow-hidden"
        >
          <div className="absolute inset-0">
            <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse" />
          </div>

          <div className="relative max-w-5xl mx-auto text-center">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl md:text-7xl font-bold mb-6">
                How Can We{' '}
                <span className="gradient-text">
                  Help You?
                </span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                Get instant answers or reach out to our support team. We're here 24/7 to help you succeed.
              </p>
            </motion.div>
          </div>
        </motion.section>

        {/* Contact Cards */}
        <section className="px-4 py-12">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6 mb-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <Card className="p-8 text-center h-full hover:shadow-xl transition-shadow border-2">
                  <div className="w-16 h-16 rounded-full gradient-hero flex items-center justify-center mx-auto mb-6">
                    <MessageCircle className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">Live Chat</h3>
                  <p className="text-muted-foreground mb-6">
                    Chat with our support team in real-time. Average response time: 2 minutes.
                  </p>
                  <Button className="w-full gradient-hero">Start Chat</Button>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                whileHover={{ y: -5 }}
              >
                <Card className="p-8 text-center h-full hover:shadow-xl transition-shadow border-2">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mx-auto mb-6">
                    <Mail className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">Email Support</h3>
                  <p className="text-muted-foreground mb-6">
                    Send us an email and get a detailed response within 24 hours.
                  </p>
                  <Button variant="outline" className="w-full" asChild>
                    <a href="mailto:support@nexqr.online">support@nexqr.online</a>
                  </Button>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                whileHover={{ y: -5 }}
              >
                <Card className="p-8 text-center h-full hover:shadow-xl transition-shadow border-2">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center mx-auto mb-6">
                    <Phone className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">Phone Support</h3>
                  <p className="text-muted-foreground mb-6">
                    Call us directly for urgent issues. Available 24/7 for Enterprise customers.
                  </p>
                  <Button variant="outline" className="w-full" asChild>
                    <a href="tel:+919465477263">+91 94654 77263</a>
                  </Button>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="px-4 py-20 bg-muted/20">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-4">
                <HelpCircle className="h-5 w-5" />
                <span className="font-medium">Frequently Asked Questions</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold">Find Quick Answers</h2>
            </motion.div>

            {faqs.map((category, catIndex) => (
              <motion.div
                key={category.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: catIndex * 0.1 }}
                className="mb-12"
              >
                <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold">
                    {catIndex + 1}
                  </span>
                  {category.category}
                </h3>

                <div className="space-y-3">
                  {category.questions.map((faq, qIndex) => {
                    const faqId = `${catIndex}-${qIndex}`;
                    const isOpen = openIndex === faqId;

                    return (
                      <motion.div
                        key={faqId}
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: qIndex * 0.05 }}
                      >
                        <button
                          onClick={() => toggleFAQ(faqId)}
                          className="w-full text-left bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-all"
                        >
                          <div className="flex items-center justify-between gap-4">
                            <h4 className="text-lg font-semibold pr-4">{faq.q}</h4>
                            <ChevronDown
                              className={cn(
                                'h-5 w-5 flex-shrink-0 transition-transform text-muted-foreground',
                                isOpen && 'rotate-180'
                              )}
                            />
                          </div>
                          {isOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="mt-4 pt-4 border-t border-border text-muted-foreground leading-relaxed"
                            >
                              {faq.a}
                            </motion.div>
                          )}
                        </button>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="px-4 py-20"
        >
          <div className="max-w-4xl mx-auto text-center gradient-hero rounded-3xl p-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Still Have Questions?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Our support team is here to help. Chat with us now or send an email.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" className="text-lg px-8">
                Start Live Chat
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 bg-white/10 text-white border-white/20 hover:bg-white/20"
                asChild
              >
                <a href="mailto:support@nexqr.online">Email Us</a>
              </Button>
            </div>
          </div>
        </motion.section>
      </div>
    </Layout>
  );
}
